/**
 * Proposal Generator Page Component (Task 107)
 *
 * Create and customize loan proposals for clients with terms, rates, and collateral details.
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { advisorClient, csrfHeader } from '../api/client';
import {
  AdvisorServiceMissingError,
  AdvisorServiceNotice,
  advisorFailure,
  advisorFetchFailure,
} from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import {
  Card,
  Heading,
  BodyText,
  FormLabel,
  Input,
  Select,
  Button,
  FormHelperText,
  Textarea,
} from '@aaimplatform/ui';


type CreateProposalRequest = components['schemas']['CreateProposalRequest'];
type ProposalDetails = components['schemas']['ProposalDetails'];
type Client = ProposalDetails['client'];

export function ProposalGeneratorPage() {
  const { proposalId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prefilledClientId = searchParams.get('clientId');
  const prefilledAmount = searchParams.get('amount');

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [generatedProposal, setGeneratedProposal] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateProposalRequest>({
    defaultValues: {
      loanAmount: prefilledAmount ? parseFloat(prefilledAmount) : 0,
      interestRate: 5.5,
      termMonths: 12,
      ltvRatio: 0.70,
      disclosures: `This proposal is subject to credit approval and collateral verification. Interest rates are variable and may change. Pledged securities remain in your custody account but are subject to restrictions during the loan term. Failure to maintain required collateral levels may result in a collateral deficiency notice requiring additional collateral or loan reduction. This is not a commitment to lend. Final terms will be provided in the loan agreement.`,
    },
  });

  // Load existing proposal if editing
  const { data: existingProposal, error: proposalError } = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: async () => {
      if (!proposalId) return null;
      const { data, error, response } = await advisorClient.GET('/api/proposals/{proposalId}', {
        params: { path: { proposalId } },
      });
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load proposal');
      return data;
    },
    enabled: !!proposalId,
  });

  // Load client data
  const { data: clientData, error: clientError } = useQuery({
    queryKey: ['client', prefilledClientId],
    queryFn: async () => {
      if (!prefilledClientId) return null;
      const { data, error, response } = await advisorClient.GET('/api/clients/{clientId}', {
        params: { path: { clientId: prefilledClientId } },
      });
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load client');
      return data;
    },
    enabled: !!prefilledClientId,
  });

  useEffect(() => {
    if (clientData) {
      setSelectedClient(clientData);
      setValue('clientId', clientData.id);
    }
  }, [clientData, setValue]);

  useEffect(() => {
    if (existingProposal) {
      setValue('clientId', existingProposal.clientId);
      setValue('loanAmount', existingProposal.loanAmount);
      setValue('interestRate', existingProposal.interestRate);
      setValue('termMonths', existingProposal.termMonths);
      setValue('ltvRatio', existingProposal.ltvRatio);
      setValue('purpose', existingProposal.purpose);
      setValue('collateralDescription', existingProposal.collateralDescription);
      if (existingProposal.specialTerms) setValue('specialTerms', existingProposal.specialTerms);
      setValue('disclosures', existingProposal.disclosures);
      setSelectedClient(existingProposal.client);
    }
  }, [existingProposal, setValue]);

  // Save proposal mutation
  const saveProposal = useMutation({
    mutationFn: async (formData: CreateProposalRequest) => {
      if (proposalId) {
        const { data, error, response } = await advisorClient.PUT('/api/proposals/{proposalId}', {
          params: { path: { proposalId } },
          body: formData,
        });
        if (error || !response.ok) throw advisorFailure(response, error, 'Failed to save proposal');
        return data;
      } else {
        const { data, error, response } = await advisorClient.POST('/api/proposals', {
          body: formData,
        });
        if (error || !response.ok) throw advisorFailure(response, error, 'Failed to create proposal');
        return data;
      }
    },
    onSuccess: (result) => {
      alert('Proposal saved successfully!');
      if (!proposalId) {
        navigate(`/proposals/${result.id}`);
      }
    },
    onError: (error: Error) => {
      // A missing service shows as the page notice.
      if (error instanceof AdvisorServiceMissingError) return;
      alert(`Error saving proposal: ${error.message}`);
    },
  });

  // Generate proposal document
  const generateProposal = useMutation({
    mutationFn: async (formData: CreateProposalRequest) => {
      // Direct fetch for binary PDF blob -- not a JSON API call, openapi-fetch not applicable.
      // credentials:'include' so the HttpOnly session cookie authenticates the request.
      // CSRF double-submit (N8): echo the csrf-token cookie as X-CSRF-Token on this
      // cookie-authenticated mutation, mirroring the openapi-fetch csrfMiddleware.
      const response = await fetch('/api/proposals/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeader('POST') },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw await advisorFetchFailure(response, 'Failed to generate PDF');
      return response.blob();
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `proposal-${Date.now()}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    },
    onError: (error: Error) => {
      if (error instanceof AdvisorServiceMissingError) return;
      alert(`Error generating proposal PDF: ${error.message}`);
    },
  });

  const serviceMissing = [proposalError, clientError, saveProposal.error, generateProposal.error].some(
    (e) => e instanceof AdvisorServiceMissingError,
  );

  const onSubmit = (data: CreateProposalRequest) => {
    saveProposal.mutate(data);
  };

  const handleGeneratePDF = () => {
    const data = watch();
    generateProposal.mutate(data as CreateProposalRequest);
  };

  const handlePreview = () => {
    const data = watch();
    const preview = generatePreview(data as CreateProposalRequest);
    setGeneratedProposal(preview);
  };

  const generatePreview = (data: CreateProposalRequest): string => {
    const monthlyPayment = calculateMonthlyPayment(
      data.loanAmount,
      data.interestRate / 100 / 12,
      data.termMonths
    );

    return `
PLEDGED-ASSET LOAN PROPOSAL

Client: ${selectedClient?.firstName} ${selectedClient?.lastName}
Email: ${selectedClient?.email}
Date: ${new Date().toLocaleDateString()}

LOAN TERMS:
- Loan Amount: ${formatCurrency(data.loanAmount)}
- Interest Rate: ${data.interestRate}% APR
- Term: ${data.termMonths} months
- Estimated Monthly Payment: ${formatCurrency(monthlyPayment)}
- Loan-to-Value Ratio: ${(data.ltvRatio * 100).toFixed(1)}%

PURPOSE:
${data.purpose || 'Not specified'}

COLLATERAL:
${data.collateralDescription || 'To be determined based on portfolio positions'}

Required Collateral Value: ${formatCurrency(data.loanAmount / data.ltvRatio)}

SPECIAL TERMS:
${data.specialTerms || 'None'}

REGULATORY COMPLIANCE:
This is a non-purpose loan structured as securities-based credit under 12 CFR 221 (Regulation U). The loan proceeds may not be used for purchasing or carrying margin securities. LTV ratios are determined by collateral type and institutional risk parameters.

DISCLOSURES:
${data.disclosures}

---
This proposal is valid for 30 days from the date above.
    `.trim();
  };

  const calculateMonthlyPayment = (
    principal: number,
    monthlyRate: number,
    months: number
  ): number => {
    if (monthlyRate === 0) return principal / months;
    return (
      (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1)
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Generate professional loan proposals for your clients
          </BodyText>
        </div>
      </div>

      {serviceMissing && <AdvisorServiceNotice className="mb-6" />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Form */}
        <Card>
          <Heading level={3} as={3} size="card" className="mb-4">Proposal Details</Heading>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Client Selection */}
            <div>
              <FormLabel htmlFor="clientId">Client *</FormLabel>
              {selectedClient ? (
                <div className="p-3 bg-recessed rounded border border-surface">
                  <div className="font-semibold text-primary">
                    {selectedClient.firstName} {selectedClient.lastName}
                  </div>
                  <div className="text-sm text-secondary">{selectedClient.email}</div>
                </div>
              ) : (
                <Select {...register('clientId', { required: true })}>
                  <option value="">Select a client...</option>
                  {/* Client options would be loaded from API */}
                </Select>
              )}
              {errors.clientId && (
                <FormHelperText className="text-error">Client is required</FormHelperText>
              )}
            </div>

            {/* Loan Amount */}
            <div>
              <FormLabel htmlFor="loanAmount">Loan Amount *</FormLabel>
              <Input
                type="number"
                step={1000}
                {...register('loanAmount', {
                  required: true,
                  min: 1000,
                })}
                placeholder="e.g., 500000"
              />
              {errors.loanAmount && (
                <FormHelperText className="text-error">
                  Valid loan amount required (minimum $1,000)
                </FormHelperText>
              )}
            </div>

            {/* Interest Rate */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FormLabel htmlFor="interestRate">Interest Rate (%) *</FormLabel>
                <Input
                  type="number"
                  step={0.1}
                  {...register('interestRate', {
                    required: true,
                    min: 0.1,
                    max: 20,
                  })}
                  placeholder="e.g., 5.5"
                />
                {errors.interestRate && (
                  <FormHelperText className="text-error">Valid rate required</FormHelperText>
                )}
              </div>

              <div>
                <FormLabel htmlFor="termMonths">Term (months) *</FormLabel>
                <Select {...register('termMonths', { required: true })}>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                  <option value="24">24 months</option>
                  <option value="36">36 months</option>
                  <option value="60">60 months</option>
                </Select>
              </div>
            </div>

            {/* LTV Ratio */}
            <div>
              <FormLabel htmlFor="ltvRatio">LTV Ratio *</FormLabel>
              <Input
                type="number"
                step={0.05}
                {...register('ltvRatio', {
                  required: true,
                  min: 0.1,
                  max: 0.8,
                })}
                placeholder="e.g., 0.70"
              />
              <FormHelperText>
                Typical range: 0.50-0.70 depending on collateral type
              </FormHelperText>
              {errors.ltvRatio && (
                <FormHelperText className="text-error">
                  LTV ratio must be between 0.1 and 0.8
                </FormHelperText>
              )}
            </div>

            {/* Purpose */}
            <div>
              <Textarea
                label="Loan Purpose"
                name="purpose"
                rows={3}
                value={watch('purpose') ?? ''}
                onChange={(val) => setValue('purpose', val)}
                placeholder="e.g., Real estate investment, business expansion, personal liquidity"
              />
            </div>

            {/* Collateral Description */}
            <div>
              <Textarea
                label="Collateral Description"
                name="collateralDescription"
                rows={3}
                value={watch('collateralDescription') ?? ''}
                onChange={(val) => setValue('collateralDescription', val)}
                placeholder="Describe the securities to be pledged as collateral"
              />
            </div>

            {/* Special Terms */}
            <div>
              <Textarea
                label="Special Terms (optional)"
                name="specialTerms"
                rows={3}
                value={watch('specialTerms') ?? ''}
                onChange={(val) => setValue('specialTerms', val)}
                placeholder="Any special conditions or terms for this proposal"
              />
            </div>

            {/* Disclosures */}
            <div>
              <Textarea
                label="Disclosures"
                name="disclosures"
                rows={4}
                required
                value={watch('disclosures') ?? ''}
                onChange={(val) => setValue('disclosures', val)}
                error={errors.disclosures ? 'Disclosures are required' : undefined}
              />
            </div>

            {/* Form Actions */}
            <div className="flex gap-3 pt-4">
              <Button type="submit" variant="primary">
                Save Proposal
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handlePreview}
              >
                Preview
              </Button>
            </div>
          </form>
        </Card>

        {/* Right: Preview */}
        <Card>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <Heading level={3} as={3} size="card">Proposal Preview</Heading>
            <Button
              variant="secondary"
              onClick={handleGeneratePDF}
              disabled={!selectedClient}
            >
              Download PDF
            </Button>
          </div>

          {generatedProposal ? (
            <div className="bg-recessed p-4 rounded border border-surface">
              <pre className="whitespace-pre-wrap text-sm text-primary font-mono">{generatedProposal}</pre>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-recessed rounded border border-surface">
              <BodyText className="text-secondary">
                Click "Preview" to see how your proposal will look
              </BodyText>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
